import {isCloseMatch, normalizeName, rankByName} from "@/helpers/import/matchNames";
import {GROUP_PATH_SEPARATOR} from "@/helpers/catalogWorkbookData";

export const GROUP = 'group';
export const OPTION_LIST = 'optionList';
export const PRODUCT = 'product';

export const CREATE = 'create';
export const IGNORE = 'ignore';
export const USE_PREFIX = 'use:';

const MAX_CANDIDATES = 8;

function validRows(sheets, name) {
    return (sheets[name]?.rows || []).filter(row => !row.excluded);
}

function addOccurrence(map, key, entry, occurrence) {
    const reference = map.get(key) || {...entry, occurrences: []};
    reference.occurrences.push(occurrence);
    map.set(key, reference);
}

function ancestorsOf(path) {
    const parts = path.split(GROUP_PATH_SEPARATOR);
    return parts.slice(0, -1).map((_, index) => parts.slice(0, index + 1).join(GROUP_PATH_SEPARATOR));
}

function collectGroupReferences(sheets) {
    const references = new Map();
    const addPath = (value, occurrence) => addOccurrence(references, normalizeName(value), {value}, occurrence);
    const add = (value, sheet, row, column) => {
        if (!value) return;
        const occurrence = {sheet, row, column};
        ancestorsOf(value).forEach(ancestor => addPath(ancestor, occurrence));
        addPath(value, occurrence);
    };
    validRows(sheets, 'Groups').forEach(row => {
        ancestorsOf(row.values.path).forEach(ancestor => addPath(ancestor, {sheet: 'Groups', row: row.rowNumber, column: 'path'}));
    });
    validRows(sheets, 'Products').forEach(row => {
        add(row.values.group, 'Products', row.rowNumber, 'group');
        (row.values.freeOptionGroups || []).forEach(path => add(path, 'Products', row.rowNumber, 'freeOptionGroups'));
        (row.values.paidOptionGroups || []).forEach(path => add(path, 'Products', row.rowNumber, 'paidOptionGroups'));
    });
    validRows(sheets, 'OptionListItems').forEach(row => add(row.values.productGroup, 'OptionListItems', row.rowNumber, 'productGroup'));
    return references;
}

function collectListReferences(sheets) {
    const references = new Map();
    const add = (value, sheet, row, column) => {
        if (!value) return;
        addOccurrence(references, normalizeName(value), {value}, {sheet, row, column});
    };
    validRows(sheets, 'Products').forEach(row => {
        (row.values.optionLists || []).forEach(name => add(name, 'Products', row.rowNumber, 'optionLists'));
    });
    validRows(sheets, 'OptionListItems').forEach(row => add(row.values.list, 'OptionListItems', row.rowNumber, 'list'));
    return references;
}

function collectProductReferences(sheets) {
    const references = new Map();
    validRows(sheets, 'OptionListItems').forEach(row => {
        const {product, productGroup} = row.values;
        if (!product) return;
        const key = `${normalizeName(product)}|${normalizeName(productGroup)}`;
        addOccurrence(references, key, {value: product, group: productGroup || ''}, {sheet: 'OptionListItems', row: row.rowNumber, column: 'product'});
    });
    return references;
}

function resolveByName(references, kind, targets, {fallback}) {
    const targetsByName = new Map(targets.map(target => [normalizeName(target.name), target]));
    const unresolved = [];
    let autoCorrected = 0;

    references.forEach((reference, key) => {
        const target = targetsByName.get(normalizeName(reference.value));
        if (target) {
            if (target.name !== reference.value) autoCorrected += 1;
            return;
        }
        const ranked = rankByName(reference.value, targets, target => target.name).slice(0, MAX_CANDIDATES);
        const best = ranked[0];
        unresolved.push({
            key: `${kind}:${key}`,
            kind,
            value: reference.value,
            occurrences: reference.occurrences,
            candidates: ranked.map(({candidate, distance}) => ({
                id: USE_PREFIX + candidate.name,
                name: candidate.name,
                source: candidate.source,
                distance,
            })),
            defaultChoice: best && isCloseMatch(reference.value, best.distance) ? USE_PREFIX + best.candidate.name : fallback,
        });
    });

    return {unresolved, autoCorrected};
}

function resolveProducts(references, products) {
    const unresolved = [];
    let autoCorrected = 0;

    references.forEach((reference, key) => {
        const sameName = products.filter(product => normalizeName(product.name) === normalizeName(reference.value));
        const matches = reference.group
            ? sameName.filter(product => normalizeName(product.group) === normalizeName(reference.group))
            : sameName;
        if (matches.length === 1) {
            if (matches[0].name !== reference.value) autoCorrected += 1;
            return;
        }
        const pool = matches.length > 1 ? matches : (sameName.length ? sameName : products);
        const ranked = rankByName(reference.value, pool, product => product.name).slice(0, MAX_CANDIDATES);
        const best = ranked[0];
        const close = best && (matches.length > 1 || sameName.length || isCloseMatch(reference.value, best.distance));
        unresolved.push({
            key: `${PRODUCT}:${key}`,
            kind: PRODUCT,
            value: reference.value,
            group: reference.group,
            ambiguous: matches.length > 1,
            occurrences: reference.occurrences,
            candidates: ranked.map(({candidate, distance}) => ({
                id: `${USE_PREFIX}${candidate.rowNumber}`,
                name: candidate.name,
                group: candidate.group,
                source: 'file',
                distance,
            })),
            defaultChoice: close ? `${USE_PREFIX}${best.candidate.rowNumber}` : IGNORE,
        });
    });

    return {unresolved, autoCorrected};
}

export function buildImportReferences(sheets, orgGroupPaths = []) {
    const fileGroups = validRows(sheets, 'Groups').map(row => ({name: row.values.path, source: 'file'}));
    const fileGroupNames = new Set(fileGroups.map(group => normalizeName(group.name)));
    const orgGroups = orgGroupPaths
        .filter(path => path && !fileGroupNames.has(normalizeName(path)))
        .map(path => ({name: path, source: 'org'}));

    const lists = validRows(sheets, 'OptionLists').map(row => ({name: row.values.name, source: 'file'}));
    const products = validRows(sheets, 'Products').map(row => ({
        name: row.values.name,
        group: row.values.group,
        rowNumber: row.rowNumber,
    }));

    const groups = resolveByName(collectGroupReferences(sheets), GROUP, [...fileGroups, ...orgGroups], {fallback: CREATE});
    const optionLists = resolveByName(collectListReferences(sheets), OPTION_LIST, lists, {fallback: CREATE});
    const items = resolveProducts(collectProductReferences(sheets), products);

    return {
        unresolved: [...groups.unresolved, ...optionLists.unresolved, ...items.unresolved],
        autoCorrected: groups.autoCorrected + optionLists.autoCorrected + items.autoCorrected,
    };
}
